import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import USMapBackdrop from "@/components/USMapBackdrop";
import { useReveal } from "@/hooks/useReveal";

/**
 * TestimonialsWall — rotating editorial peer-proof block.
 * Renders one large italic quote at a time, cross-fades every 7s.
 * Includes small dot indicators and a subtle gold framing accent.
 */

const QUOTES = [
  {
    q: "Our TheraTec chamber paid for itself in five months. The concierge install team walked our staff through pressurization the same day it landed — no consultants, no delay.",
    name: "Dr. Alessia Moreau",
    role: "Founder & Medical Director",
    biz: "Rive Aesthetics · Miami, FL",
    initial: "AM",
  },
  {
    q: "Every luxury piece we buy is judged on two things — how it makes the client feel and how the reps handle us. TheraTec is the only vendor that gets both right.",
    name: "Sanjay Kapoor",
    role: "Managing Partner",
    biz: "The Longevity Studio · Beverly Hills",
    initial: "SK",
  },
  {
    q: "We compared four hyperbaric brands. The Atlas was the only one my architect approved for the treatment suite — the finish level is genuinely furniture-grade.",
    name: "Claire Bennington",
    role: "Owner",
    biz: "Maison Bennington Med Spa · Aspen",
    initial: "CB",
  },
  {
    q: "Our regulars book the recovery room a week out. Adding the vibration plate + chamber protocol has been the single highest-margin service on our menu.",
    name: "Marcus Delaine",
    role: "COO",
    biz: "Field House Wellness · Austin",
    initial: "MD",
  },
  {
    q: "The financing side was seamless. Approved in 36 hours, deferred first payment, and Section 179 written up before we even signed. Nobody in this industry works like that.",
    name: "Dr. Nadia El-Sayed",
    role: "Founder",
    biz: "Élan Regenerative · New York, NY",
    initial: "NE",
  },
];

export default function TestimonialsWall({
  eyebrow = "The Trade Speaks",
  headline = "Loved by the med-spas ",
  headlineItalic = "you'd want to be next to.",
  variant = "cream",
  backdrop = null,
}) {
  const [i, setI] = useState(0);
  const [headerRef, headerRevealed] = useReveal();

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % QUOTES.length), 7000);
    return () => clearInterval(t);
  }, []);

  const bg = variant === "dark" ? "bg-[#1C1C1C] text-[#FAF9F6]" : "bg-[#FAF9F6] text-[#1C1C1C]";
  const sub = variant === "dark" ? "text-[#FAF9F6]/70" : "text-[#5C5C5C]";
  const border = variant === "dark" ? "border-[#FAF9F6]/10" : "border-[#E5E2DA]";

  const current = QUOTES[i];

  return (
    <section
      data-testid="testimonials-wall"
      className={`${bg} relative overflow-hidden py-16 lg:py-20 border-y ${border}`}
    >
      {/* soft champagne halo */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60vw 40vw at 50% 20%, rgba(201,169,97,0.14), transparent 60%)",
        }}
      />

      {/* Optional US map backdrop */}
      {backdrop === "usmap" && <USMapBackdrop opacity={0.85} />}
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
        <div ref={headerRef} className={`tt-reveal ${headerRevealed ? "is-revealed" : ""}`}>
          <div className="overline text-[#C9A961] inline-flex items-center gap-2">
            <span className="h-px w-6 bg-[#C9A961]" />
            {eyebrow}
            <span className="h-px w-6 bg-[#C9A961]" />
          </div>
          <h2 className="font-serif-editorial text-2xl md:text-3xl lg:text-4xl mt-4 leading-tight">
            {headline}
            <em className="italic tt-shimmer-gold">{headlineItalic}</em>
          </h2>
        </div>

        {/* Quote */}
        <div className="mt-10 relative min-h-[200px] md:min-h-[180px]">
          {QUOTES.map((qu, idx) => (
            <figure
              key={idx}
              data-testid={`testimonial-${idx}`}
              className="absolute inset-0 flex flex-col items-center justify-start transition-all duration-[900ms] ease-out"
              style={{
                opacity: idx === i ? 1 : 0,
                transform: idx === i ? "translateY(0)" : "translateY(14px)",
                pointerEvents: idx === i ? "auto" : "none",
              }}
            >
              <Quote size={22} strokeWidth={1.25} className="text-[#C9A961] mb-4" />
              <blockquote className="font-serif-editorial italic text-base md:text-lg lg:text-xl leading-[1.4] max-w-2xl">
                "{qu.q}"
              </blockquote>
              <figcaption className={`mt-5 flex items-center gap-3 ${sub}`}>
                <div
                  aria-hidden
                  className="h-9 w-9 rounded-full flex items-center justify-center border border-[#C9A961]/50 text-[10px] tracking-[0.14em] text-[#C9A961]"
                  style={{ background: "rgba(201,169,97,0.08)" }}
                >
                  {qu.initial}
                </div>
                <div className="text-left">
                  <div className={`text-sm font-medium ${variant === "dark" ? "text-[#FAF9F6]" : "text-[#1C1C1C]"}`}>
                    {qu.name}
                  </div>
                  <div className="overline mt-1">{qu.role} · {qu.biz}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-2">
          {QUOTES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              data-testid={`testimonial-dot-${idx}`}
              aria-label={`Show testimonial ${idx + 1}`}
              className="group h-2 flex items-center"
            >
              <span
                className="block h-[2px] transition-all duration-500"
                style={{
                  width: idx === i ? 40 : 18,
                  background: idx === i ? "#C9A961" : (variant === "dark" ? "rgba(250,249,246,0.25)" : "rgba(28,28,28,0.2)"),
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
